import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import DocumentUploader from "@/components/DocumentUploader";

const STEPS = ["Vorhaben", "Gründer:in", "Leistungen", "Timing & Notizen", "KYC / Compliance", "Dokumente"];

const initial = {
  legal_form_target: "GmbH",
  company_name_preferred: "",
  company_purpose: "",
  business_sector: "",
  expected_revenue_chf: 100000,
  expected_employees: 1,
  founder_address: "",
  founder_country: "Deutschland",
  nationality: "Deutsch",
  date_of_birth: "",
  services_interested: [],
  domicile_needed: false,
  accounting_needed: false,
  tax_advisory_needed: false,
  target_start_date: "",
  budget_range: "5000-10000",
  notes: "",
  referred_by: "",
  // KYC
  hr_number: "",
  ubo_name: "",
  ubo_dob: "",
  ubo_nationality: "",
  ubo_residence: "",
  ubo_share_percent: "",
  additional_ubos: false,
  funds_origin: [],
  funds_origin_other: "",
  funds_description: "",
  business_purpose_kyc: [],
  business_purpose_other: "",
  is_pep: false,
  pep_details: "",
  sanctions_confirmed: false,
};

const FUNDS_OPTIONS = [
  { key: "Erwerbstätigkeit", label: "Erwerbstätigkeit" },
  { key: "Unternehmensgewinne", label: "Unternehmensgewinne" },
  { key: "Ersparnisse", label: "Ersparnisse" },
  { key: "Verkauf von Vermögenswerten", label: "Verkauf von Vermögenswerten" },
  { key: "Erbschaft / Schenkung", label: "Erbschaft / Schenkung" },
];

const PURPOSE_OPTIONS = [
  { key: "Unternehmensgründung in der Schweiz", label: "Unternehmensgründung in der Schweiz" },
  { key: "Holdingstruktur", label: "Holdingstruktur" },
  { key: "operative Geschäftstätigkeit", label: "Operative Geschäftstätigkeit" },
  { key: "Vermögensstrukturierung", label: "Vermögensstrukturierung" },
];

const SERVICES = [
  "Firmengründung GmbH","Firmengründung AG","Vorratsgesellschaft","Treuhand monatlich",
  "Jahresbuchhaltung","Steuerberatung","Verwaltungsmandat","Firmenansiedlung",
];

export default function IntakeForm() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [previous, setPrevious] = useState([]);
  const [agbAccepted, setAgbAccepted] = useState(false);

  useEffect(() => {
    api.get("/intake/me").then(({ data }) => setPrevious(data)).catch(() => {});
  }, [done]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleService = (s) => set("services_interested", data.services_interested.includes(s) ? data.services_interested.filter((x) => x !== s) : [...data.services_interested, s]);
  const toggleArray = (field, value) => set(field, data[field].includes(value) ? data[field].filter((x) => x !== value) : [...data[field], value]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/intake", data);
      setDone(true);
      toast.success("Onboarding-Formular erfolgreich gesendet!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="text-center py-12" data-testid="intake-done">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Vielen Dank!</h2>
        <p className="text-slate-600 mb-6">Ihre Angaben wurden gespeichert und an unser Team weitergeleitet.</p>
        <button onClick={() => { setDone(false); setStep(0); setData(initial); }} className="btn-secondary">
          Weiteres Formular einreichen
        </button>
      </div>
    );
  }

  return (
    <div data-testid="intake-form">
      <h2 className="font-heading text-3xl font-bold text-[#1A2433] mb-2">Onboarding-Formular</h2>
      <p className="text-slate-600 mb-6">Schritt {step + 1} von {STEPS.length}: <strong>{STEPS[step]}</strong></p>

      {/* progress */}
      <div className="flex gap-2 mb-8">
        {STEPS.map((_, i) => (
          <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= step ? "bg-[#991B1B]" : "bg-slate-200"}`} />
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-5">
          <Select label="Geplante Rechtsform *" value={data.legal_form_target} onChange={(v) => set("legal_form_target", v)} options={["GmbH","AG","Einzelfirma","Vorratsgesellschaft","Holding","Sonstiges"]} testid="intake-legal-form" />
          <Field label="Wunsch-Firmenname (optional)" value={data.company_name_preferred} onChange={(v) => set("company_name_preferred", v)} testid="intake-name" />
          <Textarea label="Gesellschaftszweck *" value={data.company_purpose} onChange={(v) => set("company_purpose", v)} testid="intake-purpose" />
          <Field label="Branche *" value={data.business_sector} onChange={(v) => set("business_sector", v)} testid="intake-sector" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Erwarteter Jahresumsatz CHF" type="number" value={data.expected_revenue_chf} onChange={(v) => set("expected_revenue_chf", Number(v))} testid="intake-revenue" />
            <Field label="Erwartete Mitarbeitende" type="number" value={data.expected_employees} onChange={(v) => set("expected_employees", Number(v))} testid="intake-employees" />
          </div>
        </div>
      )}
      {step === 1 && (
        <div className="space-y-5">
          <Textarea label="Wohnadresse *" value={data.founder_address} onChange={(v) => set("founder_address", v)} testid="intake-address" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Land *" value={data.founder_country} onChange={(v) => set("founder_country", v)} testid="intake-country" />
            <Field label="Staatsangehörigkeit *" value={data.nationality} onChange={(v) => set("nationality", v)} testid="intake-nationality" />
          </div>
          <Field label="Geburtsdatum" type="date" value={data.date_of_birth} onChange={(v) => set("date_of_birth", v)} testid="intake-dob" />
        </div>
      )}
      {step === 2 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#1A2433] mb-2">Interessierte Leistungen</label>
            <div className="grid grid-cols-2 gap-2">
              {SERVICES.map((s) => (
                <button type="button" key={s} onClick={() => toggleService(s)}
                  className={`text-left text-sm p-3 rounded-lg border-2 transition-all ${data.services_interested.includes(s) ? "border-[#991B1B] bg-[#991B1B]/5" : "border-slate-200 hover:border-slate-300"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2 pt-2">
            <Check label="Domiziladresse in der Schweiz benötigt" value={data.domicile_needed} onChange={(v) => set("domicile_needed", v)} testid="intake-domicile" />
            <Check label="Laufende Buchhaltung benötigt" value={data.accounting_needed} onChange={(v) => set("accounting_needed", v)} testid="intake-acc" />
            <Check label="Steuerberatung gewünscht" value={data.tax_advisory_needed} onChange={(v) => set("tax_advisory_needed", v)} testid="intake-tax" />
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Gewünschter Start" type="date" value={data.target_start_date} onChange={(v) => set("target_start_date", v)} testid="intake-start" />
            <Select label="Budget" value={data.budget_range} onChange={(v) => set("budget_range", v)} options={["< 5000","5000-10000","10000-20000","> 20000","unbekannt"]} testid="intake-budget" />
          </div>
          <Textarea label="Notizen / Besonderheiten" value={data.notes} onChange={(v) => set("notes", v)} testid="intake-notes" />
          <Field label="Empfohlen durch (optional)" value={data.referred_by} onChange={(v) => set("referred_by", v)} testid="intake-ref" />
        </div>
      )}
      {step === 4 && (
        <div className="space-y-6" data-testid="intake-step-kyc">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <strong>KYC- und Compliance-Angaben</strong> nach Schweizer Geldwäschereigesetz (GwG).
            Diese Daten sind für die Erstellung von Verträgen, Vollmachten und das KYC-Formular erforderlich.
          </div>

          <Section title="Wirtschaftlich Berechtigte Person (UBO)" hint="Ausfüllen, wenn der Kunde eine Gesellschaft ist – ansonsten leer lassen.">
            <Field label="HR-Nummer (falls Firma)" value={data.hr_number} onChange={(v) => set("hr_number", v)} testid="kyc-hr" />
            <Field label="Name UBO" value={data.ubo_name} onChange={(v) => set("ubo_name", v)} testid="kyc-ubo-name" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Geburtsdatum UBO" type="date" value={data.ubo_dob} onChange={(v) => set("ubo_dob", v)} testid="kyc-ubo-dob" />
              <Field label="Nationalität UBO" value={data.ubo_nationality} onChange={(v) => set("ubo_nationality", v)} testid="kyc-ubo-nat" />
            </div>
            <Field label="Wohnsitz UBO" value={data.ubo_residence} onChange={(v) => set("ubo_residence", v)} testid="kyc-ubo-res" />
            <Field label="Beteiligung in %" type="number" value={data.ubo_share_percent} onChange={(v) => set("ubo_share_percent", v)} testid="kyc-ubo-pct" />
            <Check label="Es existieren weitere wirtschaftlich berechtigte Personen (Beiblatt erforderlich)" value={data.additional_ubos} onChange={(v) => set("additional_ubos", v)} testid="kyc-ubo-additional" />
          </Section>

          <Section title="Herkunft der Mittel" hint="Bitte alle zutreffenden Quellen markieren.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FUNDS_OPTIONS.map((opt) => (
                <button type="button" key={opt.key}
                  onClick={() => toggleArray("funds_origin", opt.key)}
                  className={`text-left text-sm p-2.5 rounded-lg border-2 transition-all ${data.funds_origin.includes(opt.key) ? "border-[#991B1B] bg-[#991B1B]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`kyc-funds-${opt.key.replace(/[^a-z]/gi,"")}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <Field label="Sonstige Mittelherkunft (optional)" value={data.funds_origin_other} onChange={(v) => set("funds_origin_other", v)} testid="kyc-funds-other" />
            <Textarea label="Beschreibung (optional, aber empfohlen)" value={data.funds_description} onChange={(v) => set("funds_description", v)} testid="kyc-funds-desc" />
          </Section>

          <Section title="Zweck der Geschäftsbeziehung">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PURPOSE_OPTIONS.map((opt) => (
                <button type="button" key={opt.key}
                  onClick={() => toggleArray("business_purpose_kyc", opt.key)}
                  className={`text-left text-sm p-2.5 rounded-lg border-2 transition-all ${data.business_purpose_kyc.includes(opt.key) ? "border-[#991B1B] bg-[#991B1B]/5" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`kyc-purpose-${opt.key.replace(/[^a-z]/gi,"")}`}>
                  {opt.label}
                </button>
              ))}
            </div>
            <Field label="Sonstiger Zweck (optional)" value={data.business_purpose_other} onChange={(v) => set("business_purpose_other", v)} testid="kyc-purpose-other" />
          </Section>

          <Section title="Politisch exponierte Person (PEP)">
            <Check
              label="Ja, ich oder eine wirtschaftlich berechtigte Person ist PEP"
              value={data.is_pep}
              onChange={(v) => set("is_pep", v)}
              testid="kyc-pep"
            />
            {data.is_pep && (
              <Textarea label="PEP-Details (Funktion, Land, Verbindungen)" value={data.pep_details} onChange={(v) => set("pep_details", v)} testid="kyc-pep-details" />
            )}
          </Section>

          <Section title="Sanktionen / Risikoländer">
            <Check
              label="Ich bestätige, dass weder ich noch wirtschaftlich Berechtigte unter Sanktionen stehen oder Verbindungen zu sanktionierten Personen/Ländern bestehen."
              value={data.sanctions_confirmed}
              onChange={(v) => set("sanctions_confirmed", v)}
              testid="kyc-sanctions"
            />
          </Section>
        </div>
      )}
      {step === 5 && (
        <div data-testid="intake-step-documents">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
            <p className="text-sm text-slate-700">
              <strong>Optional:</strong> Laden Sie hier bereits jetzt benötigte Dokumente hoch
              (Ausweis, Pass, Handelsregisterauszug etc.). Sie können dies auch nachträglich
              jederzeit unter <em>„Meine Dokumente"</em> im Portal nachholen.
            </p>
          </div>
          <DocumentUploader compact />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-between mt-8 pt-6 border-t-2 border-slate-200">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed transition"
          data-testid="intake-prev"
        >
          <ChevronLeft className="w-5 h-5" /> Zurück
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep((s) => s + 1)}
            className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#991B1B] hover:bg-[#7c1414] text-white font-bold rounded-lg shadow-lg shadow-red-900/20 hover:shadow-xl transition"
            data-testid="intake-next"
          >
            Weiter <ChevronRight className="w-5 h-5" />
          </button>
        ) : (
          <div className="flex flex-col items-end gap-3" data-testid="intake-final-block">
            <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer max-w-md select-none" data-testid="intake-agb-wrapper">
              <input
                type="checkbox"
                checked={agbAccepted}
                onChange={(e) => setAgbAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#991B1B]"
                data-testid="intake-agb-checkbox"
              />
              <span>
                Ich habe die <a href="/impressum#agb" target="_blank" rel="noopener noreferrer" className="text-[#991B1B] underline hover:no-underline">AGB</a> und die{" "}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#991B1B] underline hover:no-underline">Datenschutzerklärung</a> gelesen und akzeptiere diese.
              </span>
            </label>
            <button
              onClick={submit}
              disabled={busy || !agbAccepted}
              className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#991B1B] hover:bg-[#7c1414] text-white font-bold rounded-lg shadow-lg shadow-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="intake-submit"
            >
              {busy ? "Sende…" : "Absenden"}
            </button>
          </div>
        )}
      </div>

      {previous.length > 0 && (
        <div className="mt-10 pt-8 border-t border-slate-200">
          <h3 className="font-heading text-lg font-semibold text-[#1A2433] mb-3">Frühere Einreichungen</h3>
          <ul className="space-y-2">
            {previous.map((p) => (
              <li key={p.id} className="text-sm text-slate-600 flex justify-between bg-slate-50 px-4 py-2 rounded-md">
                <span>{p.data.legal_form_target} – {p.data.business_sector}</span>
                <span className="text-slate-400">{new Date(p.created_at).toLocaleDateString("de-CH")}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]" data-testid={testid} />
    </div>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}</label>
      <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B]" data-testid={testid} />
    </div>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#1A2433] mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#991B1B] bg-white" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Check({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer p-2 rounded-md hover:bg-slate-50">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#991B1B]" data-testid={testid} />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-white">
      <h4 className="font-semibold text-[#1A2433] text-sm mb-1">{title}</h4>
      {hint && <p className="text-xs text-slate-500 mb-3">{hint}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}
